// Baselayer Results Explorer — Watchlists tab. One block per watchlist code:
// name + Hit/No Hit badge (by count), with an expandable details list
// (program / remarks).

import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { EmptyState } from "@/design-system/components/ui";
import { MatchBadge, countToHit } from "../MatchBadge";
import { parseWatchlists, type ParsedKyb, type ParsedWatchlist } from "../parse";

export function WatchlistsTab({ kyb }: { kyb: ParsedKyb }) {
  // Watchlists live on the KYB rawResponseJson under watchlist_hits.
  const watchlists = parseWatchlists(
    kyb.raw ? JSON.stringify(kyb.raw) : null,
  );

  if (watchlists.length === 0) {
    return (
      <EmptyState
        title="No watchlist hits"
        description="This subject did not match any monitored watchlists."
      />
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      {watchlists.map((w, i) => (
        <WatchlistRow key={`${w.code ?? w.name}-${i}`} watchlist={w} />
      ))}
    </div>
  );
}

function WatchlistRow({ watchlist }: { watchlist: ParsedWatchlist }) {
  const [open, setOpen] = useState(false);
  const hasDetails = watchlist.details.length > 0;

  return (
    <div className="rounded-lg border border-border-light bg-white-pure shadow-sm">
      <button
        type="button"
        onClick={() => hasDetails && setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-space-3 px-space-4 py-space-3 text-left"
        aria-expanded={hasDetails ? open : undefined}
      >
        <span className="flex items-center gap-space-2">
          {hasDetails ? (
            open ? (
              <ChevronDown size={16} aria-hidden />
            ) : (
              <ChevronRight size={16} aria-hidden />
            )
          ) : (
            <span className="w-4" />
          )}
          <span className="text-small font-medium text-onyx">
            {watchlist.name ?? watchlist.code ?? "Watchlist"}
          </span>
          {watchlist.code && watchlist.name && (
            <span className="text-caption text-gray-mid">{watchlist.code}</span>
          )}
        </span>
        <MatchBadge kind={countToHit(watchlist.count)} />
      </button>
      {open && hasDetails && (
        <ul className="m-0 list-none border-t border-border-light px-space-4 py-space-3 flex flex-col gap-space-2">
          {watchlist.details.map((d, i) => (
            <li key={i} className="text-small text-onyx">
              <span className="font-medium">{d.program ?? "Program"}</span>
              {d.remarks && (
                <span className="text-gray-mid"> — {d.remarks}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
